import { useMutation, useQuery } from '@tanstack/react-query';

import {
  CreateFacilitiesRequest,
  CreateFacilitiesResponse,
  GetFacilitiesRequest,
  GetFacilitiesResponse,
  UpdateFacilitiesRequest,
  UpdateFacilitiesResponse,
} from './apiTypes/endpoints/facilities';
import { ApiError } from './apiTypes/misc';
import {
  createFacilitiesPath,
  getFacilitiesPath,
  updateFacilitiesPath,
} from './apiTypes/paths/facilities';
import { RQUseMutationOptions, RQUseMutationResult, RQUseQueryOptions } from './apiTypes/query';
import * as api from './requests';

const FACILITIES_URL = '/facilities/';

export const GET_FACILITIES_QUERY = `${FACILITIES_URL}GET_FACILITIES_QUERY`;

export function getFacilities({ schoolId }: GetFacilitiesRequest): Promise<GetFacilitiesResponse> {
  return api.get(getFacilitiesPath({ school_id: schoolId }));
}

export const useGetFacilitiesQuery = (
  params: GetFacilitiesRequest,
  options?: RQUseQueryOptions<GetFacilitiesResponse>,
) => {
  return useQuery<GetFacilitiesResponse, ApiError>(
    [GET_FACILITIES_QUERY, params],
    () => getFacilities(params),
    options,
  );
};

export const CREATE_FACILITIES_MUTATION = `${FACILITIES_URL}CREATE_FACILITIES_MUTATION`;

export function createFacilities(data: CreateFacilitiesRequest): Promise<CreateFacilitiesResponse> {
  return api.post(createFacilitiesPath(), data);
}

export const useCreateFacilitiesMutation = (
  options?: RQUseMutationOptions<CreateFacilitiesResponse, CreateFacilitiesRequest>,
): RQUseMutationResult<CreateFacilitiesResponse, CreateFacilitiesRequest> => {
  return useMutation(
    [CREATE_FACILITIES_MUTATION],
    (params: CreateFacilitiesRequest) => createFacilities(params),
    options,
  );
};

export const UPDATE_FACILITIES_MUTATION = `${FACILITIES_URL}UPDATE_FACILITIES_MUTATION`;

export function updateFacilities({
  params,
  data,
}: UpdateFacilitiesRequest): Promise<UpdateFacilitiesResponse> {
  return api.put(updateFacilitiesPath({ school_id: params.schoolId }), data);
}

export const useUpdateFacilitiesMutation = (
  options?: RQUseMutationOptions<UpdateFacilitiesResponse, UpdateFacilitiesRequest>,
): RQUseMutationResult<UpdateFacilitiesResponse, UpdateFacilitiesRequest> => {
  return useMutation(
    [UPDATE_FACILITIES_MUTATION],
    (params: UpdateFacilitiesRequest) => updateFacilities(params),
    options,
  );
};
